import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconArrowLeft, IconLoader2, IconUsers, IconMail, IconRefresh, IconTrash,
  IconSearch, IconShieldCheck, IconAlertTriangle,
} from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";

const ROLE_LABEL_ES = {
  familia: "Familia",
  docente: "Docente",
  sombra: "Maestra Sombra",
  psicologa: "Psicóloga",
  escuela: "Escuela",
};

export default function AdminUsers() {
  const { lang } = useI18n();
  const { user: me } = useAuth();
  const nav = useNavigate();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [audit, setAudit] = useState(null);
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    const myReqId = ++reqIdRef.current;
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (q.trim()) params.set("q", q.trim());
      if (role) params.set("role", role);
      const { data } = await api.get(`/admin/users?${params.toString()}`);
      // Discard stale responses when filters changed while awaiting.
      if (myReqId !== reqIdRef.current) return;
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      if (myReqId !== reqIdRef.current) return;
      setError(formatApiError(e));
    }
    finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [q, role]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/audit/admins");
        setAudit(data);
      } catch {
        setAudit(null);
      }
    })();
  }, []);

  const fmt = (iso) =>
    iso ? new Date(iso).toLocaleString(lang === "es" ? "es-HN" : "en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";

  return (
    <>
      <div className="subbar">
        <button className="btn-ghost btn text-[12px] px-2 py-1" onClick={() => nav("/app")} data-testid="admin-users-back">
          <IconArrowLeft size={13} /> {lang === "es" ? "Volver al inicio" : "Back"}
        </button>
        <span className="t">{lang === "es" ? "Usuarios · Gestión de cuentas" : "Users · Account management"}</span>
      </div>

      <div className="wrap-app max-w-[1100px] py-8" data-testid="admin-users-page">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div>
            <h2 className="font-display text-[24px] font-medium flex items-center gap-2">
              <IconUsers size={20} /> {lang === "es" ? "Usuarios de la plataforma" : "Platform users"}
            </h2>
            <p className="text-tert text-[12.5px] mt-1">
              {lang === "es"
                ? `Total: ${total} · Podés filtrar por nombre, email o rol.`
                : `Total: ${total} · Filter by name, email or role.`}
            </p>
          </div>
          <button className="btn-ghost btn text-[12px]" onClick={load} data-testid="admin-users-refresh">
            <IconRefresh size={13} /> {lang === "es" ? "Actualizar" : "Refresh"}
          </button>
        </div>

        <div className="card mb-4">
          <div className="grid sm:grid-cols-[2fr_1fr] gap-3">
            <div className="field mb-0">
              <label className="lbl">{lang === "es" ? "Buscar" : "Search"}</label>
              <div className="relative">
                <IconSearch size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-tert" />
                <input
                  className="input pl-8"
                  placeholder={lang === "es" ? "email o nombre" : "email or name"}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="admin-users-q"
                />
              </div>
            </div>
            <div className="field mb-0">
              <label className="lbl">{lang === "es" ? "Rol" : "Role"}</label>
              <select
                className="select-css"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                data-testid="admin-users-role"
              >
                <option value="">{lang === "es" ? "Todos" : "All"}</option>
                <option value="familia">Familia</option>
                <option value="docente">Docente</option>
                <option value="sombra">Sombra</option>
                <option value="psicologa">Psicóloga</option>
                <option value="escuela">Escuela</option>
              </select>
            </div>
          </div>
        </div>

        {audit && (
          <div
            className={`card mb-4 border-l-[3px] ${audit.single_admin_ok ? "border-l-teal bg-teal-soft/40" : "border-l-errtext bg-errbg"}`}
            data-testid="admin-audit-card"
          >
            <div className="flex items-start gap-3">
              {audit.single_admin_ok ? (
                <IconShieldCheck size={20} className="text-teal-deep shrink-0 mt-0.5" />
              ) : (
                <IconAlertTriangle size={20} className="text-errtext shrink-0 mt-0.5" />
              )}
              <div className="min-w-0 flex-1">
                <p className={`text-[13.5px] font-medium ${audit.single_admin_ok ? "text-teal-deep" : "text-errtext"}`}>
                  {audit.single_admin_ok
                    ? (lang === "es" ? "Auditoría OK: administradora única confirmada" : "Audit OK: single administrator confirmed")
                    : (lang === "es" ? `Alerta: ${audit.count} cuentas con privilegios de administrador` : `Alert: ${audit.count} accounts have admin privileges`)}
                </p>
                <p className="text-[12px] text-tert mt-1 font-mono">
                  {lang === "es" ? "Esperado" : "Expected"}: {audit.expected_admin_email}
                </p>
                {audit.items && audit.items.length > 0 && (
                  <ul className="mt-2 space-y-1 text-[12.5px]">
                    {audit.items.map((a) => (
                      <li key={a.id} className="flex items-center gap-2" data-testid={`audit-admin-${a.id}`}>
                        <IconShieldCheck size={11} className="text-teal-deep" />
                        <span className="font-mono">{a.email}</span>
                        <span className="text-tert">— {a.name}</span>
                        {a.email.toLowerCase() !== audit.expected_admin_email && (
                          <span className="tag bg-errbg text-errtext text-[10.5px]">
                            {lang === "es" ? "inesperado" : "unexpected"}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {audit.unexpected_admins && audit.unexpected_admins.length > 0 && (
                  <p className="text-[12px] text-errtext mt-2">
                    {lang === "es"
                      ? "Revocá is_admin=true a estas cuentas desde la consola de MongoDB."
                      : "Revoke is_admin=true on these accounts via MongoDB console."}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {error && <p className="text-errtext text-[13px] mb-4">{error}</p>}
        {loading ? (
          <div className="card"><IconLoader2 className="animate-spin" /></div>
        ) : items.length === 0 ? (
          <p className="text-tert text-[13px]">{lang === "es" ? "Sin usuarios." : "No users."}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="admin-users-table">
              <thead>
                <tr className="text-left text-tert border-b border-line">
                  <th className="p-2">{lang === "es" ? "Nombre" : "Name"}</th>
                  <th className="p-2">Email</th>
                  <th className="p-2">{lang === "es" ? "Roles" : "Roles"}</th>
                  <th className="p-2">{lang === "es" ? "Creado" : "Created"}</th>
                  <th className="p-2 text-right">{lang === "es" ? "Acciones" : "Actions"}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => {
                  const isMe = u.id === me?.id;
                  const roles = (u.roles && u.roles.length ? u.roles : [u.role]).filter(Boolean);
                  return (
                    <tr key={u.id} className="border-b border-line/70" data-testid={`user-row-${u.id}`}>
                      <td className="p-2 align-top">
                        <p className="font-medium">
                          {u.name || "—"}
                          {u.is_admin && (
                            <span className="tag ml-2 bg-teal-soft text-teal-deep" title="Administrador">
                              <IconShieldCheck size={10} className="inline" /> admin
                            </span>
                          )}
                        </p>
                      </td>
                      <td className="p-2 align-top font-mono text-[12px]">
                        <IconMail size={11} className="inline mr-1 text-tert" />{u.email}
                      </td>
                      <td className="p-2 align-top">
                        <div className="flex flex-wrap gap-1">
                          {roles.map((r) => (
                            <span key={r} className="tag text-[11px]">{ROLE_LABEL_ES[r] || r}</span>
                          ))}
                        </div>
                      </td>
                      <td className="p-2 align-top text-tert text-[12px] font-mono">{fmt(u.created_at)}</td>
                      <td className="p-2 align-top text-right">
                        {u.is_admin || isMe ? (
                          <span className="text-tert text-[11.5px]" title={isMe ? (lang === "es" ? "Tu cuenta" : "Your account") : (lang === "es" ? "Otro admin" : "Other admin")}>—</span>
                        ) : (
                          <button
                            className="btn-ghost btn text-[11px] px-2 py-1 text-errtext"
                            onClick={() => setDeleteTarget(u)}
                            data-testid={`user-delete-${u.id}`}
                            title={lang === "es" ? "Eliminar usuario" : "Delete user"}
                          >
                            <IconTrash size={11} /> {lang === "es" ? "Eliminar" : "Delete"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDeleteModal
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={async () => {
          await api.delete(`/admin/users/${deleteTarget.id}`);
          setDeleteTarget(null);
          await load();
        }}
        title={lang === "es" ? "Eliminar usuario" : "Delete user"}
        description={
          deleteTarget
            ? (lang === "es"
                ? `Se eliminará la cuenta de <b>${deleteTarget.name || deleteTarget.email}</b> (<span class="font-mono">${deleteTarget.email}</span>) junto con sus suscripciones al boletín, accesos otorgados y progreso de cursos. Los registros contables (compras) se conservan para auditoría. Esta acción es irreversible.`
                : `Account for <b>${deleteTarget.name || deleteTarget.email}</b> (<span class="font-mono">${deleteTarget.email}</span>) will be removed along with newsletter subscriptions, granted accesses and course progress. Purchase records are kept for audit. This is irreversible.`)
            : ""
        }
        testId="admin-user-delete-modal"
      />
    </>
  );
}
